import { surveyElementFrom } from './survey_element.transformer';
import { buildSurveyPreviewLink } from '~/tools/survey_helper';
import { convert } from '~/tools/convert';

/**
 * @module survey.transformer
 */
const surveyFrom = (survey) => {
  // The graphql will automatically do this convertion. So do it here to keep consistency with surveyEditFrom
  const startTimeUTC = survey.startTimeUTC ? Date.parse(survey.startTimeUTC) : null;
  const endTimeUTC = survey.endTimeUTC ? Date.parse(survey.endTimeUTC) : null;

  // REST survey payloads expose recipients as an array ({ employeeId, surveyInviteDate }),
  // not the id->date dictionary the participant list consumes. Build that dictionary here.
  // surveyInviteDate is null when the recipient was added but never invited; otherwise it is
  // an ISO string (REST JSON) which we normalise to a ms-epoch string so it matches the GraphQL
  // fetch path (the list formats dates via `new Date(Number(...))`).
  const recipientInvitationDates = Array.isArray(survey.recipients)
    ? survey.recipients.reduce((dates, recipient) => {
        dates[recipient.employeeId] = recipient.surveyInviteDate
          ? String(new Date(recipient.surveyInviteDate).getTime())
          : null;
        return dates;
      }, {})
    : survey.recipientInvitationDates;

  // surveyFrom is used by rest endpoints, don't mix it with surveysFrom or surveyEditFrom
  return {
    id: survey.id,
    name: survey.name,
    surveyStatusId: survey.surveyStatusId,
    languageCodes: survey.languageCodes,
    companyId: survey.companyId,
    surveyTypeId: survey.surveyTypeId,
    sendingEmailAddressId: survey.sendingEmailAddressId,
    timezoneId: survey.timezoneId,
    showResults: !!survey.showResults,
    anonymousConversation: !!survey.anonymousConversation,
    startTimeUTC,
    endTimeUTC,
    fromEmailName: survey.fromEmailName,
    hashCode: survey.hashCode,
    emailLanguages: survey.emailLanguages,
    languageIds: survey.emailLanguages.map((emailLang) => emailLang.languageId),
    recipientIds: survey.recipientIds,
    recipientInvitationDates,
    combineLanguages: survey.combineLanguages,
    isContinuous: survey.isContinuous,
    invitationExpiryIn: survey.invitationExpiryIn,
    disableEmailSending: survey.disableEmailSending,
    audienceMode: survey.audienceMode,
    openSurveyUuid: survey.openSurveyUuid,
    openSurveyAccessCode: survey.openSurveyAccessCode,
  };
};

/**
 * Map list of surveys returned by server to usable format by front-end UI.
 *
 * @example surveys (from server)
 * [
 *   {
 *     "id": 123,
 *     "surveyName": "My Survey Name",
 *     "typeId": 123,
 *     "surveyTypeId": 123
 *   }
 * ]
 */
const surveysFrom = (list = []) => {
  // surveysFrom currently used by survey dashboard which is a legacy graphql endpoint, don't mix it with surveyFrom
  const surveyList = list.map((survey) => ({
    // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
    id: survey.id,
    // @ts-expect-error TS(2339): Property 'surveyName' does not exist on type 'neve... Remove this comment to see the full error message
    name: survey.surveyName,
    // @ts-expect-error TS(2339): Property 'typeId' does not exist on type 'never'.
    surveyTypeId: survey.typeId,
    // @ts-expect-error TS(2339): Property 'surveyStatusId' does not exist on type '... Remove this comment to see the full error message
    surveyStatusId: survey.surveyStatusId,
    // @ts-expect-error TS(2339): Property 'startTimeUTC' does not exist on type 'ne... Remove this comment to see the full error message
    startTimeUTC: survey.startTimeUTC,
    // @ts-expect-error TS(2339): Property 'supportLanguages' does not exist on type... Remove this comment to see the full error message
    supportLanguages: survey.supportLanguages,
    // TODO: fill out other attributes as needed
    combineLanguages: survey.combineLanguages || false,
    timezoneId: survey.timezoneId,
    isContinuous: survey.isContinuous,
    invitationExpiryIn: survey.invitationExpiryIn,
  }));

  return surveyList;
};

// This is used for get survey endpoint
// the data structure is different with dashboard surveys, so need to keep it separated
const surveyFromSurveyEdit = (survey) => {
  const hashCode = survey.hashCode;
  const previewLink = buildSurveyPreviewLink(hashCode);
  const surveyStatusId = survey.status.id;
  const recipientIds = survey.employees.map((employee) => employee.id);
  const recipientInvitationDates = convert.toDictValue(survey.employees, 'id', 'surveyInviteDate');
  const recipientAddedDates = convert.toDictValue(survey.employees, 'id', 'addedDate');
  const fromEmailName = survey.fromEmailName || '';
  const name = survey.namesPerLanguage[0].surveyName;
  const surveyTypeId = survey.types[0].id;
  const showResults = survey.showResults;
  const anonymousConversation = survey.anonymousConversation;
  const languageIds = survey.namesPerLanguage.map((lang) => Number(lang.language.id));
  const languageCodes = survey.namesPerLanguage.map((lang) => lang.language.langISOCode);
  const sendingEmailAddressId = survey.sendingEmailAddress?.id || null;
  const timezoneId = survey.timezone?.id || null;
  const combineLanguages = survey.combineLanguages || false;
  const disableEmailSending = !!survey.disableEmailSending;
  const isContinuous = !!survey.isContinuous;
  const audienceMode = survey.audienceMode;
  const openSurveyUuid = survey.openSurveyUuid;
  const openSurveyAccessCode = survey.openSurveyAccessCode;
  const invitationExpiryIn = survey.invitationExpiryIn;

  const transformedSurveyElements = survey.questions
    ? survey.questions.map((question) => surveyElementFrom(question, languageCodes))
    : [];

  const startTimeUTC = survey.startTimeUTC || null;
  const endTimeUTC = survey.endTimeUTC || null;

  // ToDo: refactor to translations
  const emailLanguages = survey.namesPerLanguage.map((lang) => ({
    languageId: Number(lang.language.id),
    languageCode: lang.language.langISOCode,
    subject: lang.emailSubject,
    content: lang.emailBody,
  }));

  return {
    id: survey.id,
    name,
    surveyTypeId,
    hashCode,
    previewLink,
    surveyStatusId,
    recipientIds,
    recipientInvitationDates,
    recipientAddedDates,
    fromEmailName,
    showResults,
    anonymousConversation,
    languageIds,
    languageCodes,
    surveyElements: transformedSurveyElements,
    emailLanguages,
    sendingEmailAddressId,
    timezoneId,
    startTimeUTC,
    endTimeUTC,
    combineLanguages,
    disableEmailSending,
    isContinuous,
    audienceMode,
    openSurveyUuid,
    openSurveyAccessCode,
    invitationExpiryIn,
    lifecycleSurveyTypeId: survey.lifecycleSurveyTypeId,
    audienceMode: survey.audienceMode,
  };
};

export { surveysFrom, surveyFrom, surveyFromSurveyEdit };
